import _css from '@emotion/css';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import { jsx } from '@emotion/core';
import { useRef } from 'react';

function getClientPosition(e) {
  var touches = e.touches;

  if (touches && touches.length) {
    var finger = touches[0];
    return {
      x: finger.clientX,
      y: finger.clientY
    };
  }

  return {
    x: e.clientX,
    y: e.clientY
  };
}

var track = {
  position: 'relative',
  display: 'inline-block',
  backgroundColor: '#ddd',
  borderRadius: 5,
  userSelect: 'none',
  boxSizing: 'border-box'
};
var active = {
  position: 'absolute',
  backgroundColor: '#5e72e4',
  borderRadius: 5,
  userSelect: 'none',
  boxSizing: 'border-box'
};
var thumb = {
  position: 'relative',
  display: 'block',
  content: '""',
  width: 18,
  height: 18,
  backgroundColor: '#fff',
  borderRadius: '50%',
  boxShadow: '0 1px 1px rgba(0,0,0,.5)',
  userSelect: 'none',
  boxSizing: 'border-box'
};
var styles = {
  x: {
    track: _extends({}, track, {
      width: 200,
      height: 10
    }),
    active: _extends({}, active, {
      top: 0,
      height: '100%'
    }),
    thumb: _extends({}, thumb)
  },
  y: {
    track: _extends({}, track, {
      width: 10,
      height: 200
    }),
    active: _extends({}, active, {
      left: 0,
      width: '100%'
    }),
    thumb: _extends({}, thumb)
  },
  xy: {
    track: {
      position: 'relative',
      overflow: 'hidden',
      width: 200,
      height: 200,
      backgroundColor: '#5e72e4',
      borderRadius: 0
    },
    active: {},
    thumb: _extends({}, thumb)
  },
  disabled: {
    opacity: 0.5
  }
};

var Slider = function Slider(_ref) {
  var disabled = _ref.disabled,
      axis = _ref.axis,
      x = _ref.x,
      y = _ref.y,
      xmin = _ref.xmin,
      xmax = _ref.xmax,
      ymin = _ref.ymin,
      ymax = _ref.ymax,
      xstep = _ref.xstep,
      ystep = _ref.ystep,
      onChange = _ref.onChange,
      onDragStart = _ref.onDragStart,
      onDragEnd = _ref.onDragEnd,
      onClick = _ref.onClick,
      customStyles = _ref.styles,
      props = _objectWithoutPropertiesLoose(_ref, ["disabled", "axis", "x", "y", "xmin", "xmax", "ymin", "ymax", "xstep", "ystep", "onChange", "onDragStart", "onDragEnd", "onClick", "styles"]);

  var container = useRef(null);
  var handle = useRef(null);
  var start = useRef({});
  var offset = useRef({});

  function getPosition() {
    var top = (y - ymin) / (ymax - ymin) * 100;
    var left = (x - xmin) / (xmax - xmin) * 100;
    if (top > 100) top = 100;
    if (top < 0) top = 0;
    if (axis === 'x') top = 0;
    top += '%';
    if (left > 100) left = 100;
    if (left < 0) left = 0;
    if (axis === 'y') left = 0;
    left += '%';
    return {
      top: top,
      left: left
    };
  }

  function change(_ref2) {
    var top = _ref2.top,
        left = _ref2.left;
    if (!onChange) return;

    var _container$current$ge = container.current.getBoundingClientRect(),
        width = _container$current$ge.width,
        height = _container$current$ge.height;

    var dx = 0;
    var dy = 0;
    if (left < 0) left = 0;
    if (left > width) left = width;
    if (top < 0) top = 0;
    if (top > height) top = height;

    if (axis === 'x' || axis === 'xy') {
      dx = left / width * (xmax - xmin);
    }

    if (axis === 'y' || axis === 'xy') {
      dy = top / height * (ymax - ymin);
    }

    var x = (dx !== 0 ? parseInt(dx / xstep, 10) * xstep : 0) + xmin;
    var y = (dy !== 0 ? parseInt(dy / ystep, 10) * ystep : 0) + ymin;
    onChange({
      x: x,
      y: y
    });
  }

  function handleMouseDown(e) {
    if (disabled) return;
    e.preventDefault();
    var dom = handle.current;
    var clientPos = getClientPosition(e);
    start.current = {
      x: dom.offsetLeft,
      y: dom.offsetTop
    };
    offset.current = {
      x: clientPos.x,
      y: clientPos.y
    };
    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchmove', handleDrag, {
      passive: false
    });
    document.addEventListener('touchend', handleDragEnd);
    document.addEventListener('touchcancel', handleDragEnd);

    if (onDragStart) {
      onDragStart(e);
    }
  }

  function getPos(e) {
    var clientPos = getClientPosition(e);
    var left = clientPos.x + start.current.x - offset.current.x;
    var top = clientPos.y + start.current.y - offset.current.y;
    return {
      left: left,
      top: top
    };
  }

  function handleDrag(e) {
    if (disabled) return;
    e.preventDefault();
    change(getPos(e));
  }

  function handleDragEnd(e) {
    if (disabled) return;
    e.preventDefault();
    document.removeEventListener('mousemove', handleDrag);
    document.removeEventListener('mouseup', handleDragEnd);
    document.removeEventListener('touchmove', handleDrag, {
      passive: false
    });
    document.removeEventListener('touchend', handleDragEnd);
    document.removeEventListener('touchcancel', handleDragEnd);

    if (onDragEnd) {
      onDragEnd(e);
    }
  }

  function handleClick(e) {
    if (disabled) return;
    var clientPos = getClientPosition(e);
    var rect = container.current.getBoundingClientRect();
    change({
      left: clientPos.x - rect.left,
      top: clientPos.y - rect.top
    });
    if (onClick) onClick(e);
  }

  var pos = getPosition();
  var valueStyle = {};
  if (axis === 'x') valueStyle.width = pos.left;
  if (axis === 'y') valueStyle.height = pos.top;
  var styles$1 = {
    track: _extends({}, styles[axis].track, customStyles.track),
    active: _extends({}, styles[axis].active, customStyles.active),
    thumb: _extends({}, styles[axis].thumb, customStyles.thumb),
    disabled: _extends({}, styles.disabled, customStyles.disabled)
  };
  styles$1.thumb = {
    position: 'absolute',
    '&:after': _extends({}, styles$1.thumb, {
      top: axis === 'x' ? (styles$1.track.height - styles$1.thumb.height) / 2 : -styles$1.thumb.height / 2,
      left: axis === 'y' ? (styles$1.track.width - styles$1.thumb.width) / 2 : -styles$1.thumb.width / 2
    })
  };
  return jsx("div", _extends({}, props, {
    ref: container,
    css:
    /*#__PURE__*/
    _css([styles$1.track, disabled && styles$1.disabled], "label:Slider;" + (process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */")),
    onClick: handleClick
  }), jsx("div", {
    css: styles$1.active,
    style: valueStyle
  }), jsx("div", {
    ref: handle,
    css: styles$1.thumb,
    style: pos,
    onTouchStart: handleMouseDown,
    onMouseDown: handleMouseDown,
    onClick: function onClick(e) {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
    }
  }));
};

Slider.defaultProps = {
  disabled: false,
  axis: 'x',
  x: 50,
  xmin: 0,
  xmax: 100,
  y: 50,
  ymin: 0,
  ymax: 100,
  xstep: 1,
  ystep: 1,
  styles: {}
};
export default Slider;